// 36-month revenue & cost breakdown — stacked bars + sorted line list.
// Iterates dynamically over `aggregateLines(rows)`, so new revenue / cost
// lines added to the engine flow through without touching this component.

import { useMemo, useState } from 'react';
import { aggregateLines } from '../../engine/index.js';

const REVENUE_LABEL = {
  proSub: 'Pro subscription',
  interchange: 'Card spend',
  fx: 'FX margin',
  topup: 'Top-up margin',
  swap: 'Conversion margin',
  earn: 'Pulsar yield spread',
  aiOverage: 'AI agent overage',
  aiOutcome: 'AI outcome-based',
  aiReferral: 'AI merchant referral',
  b2bPartnerCampaigns: 'Partner campaigns',
  b2bClosedLoop: 'Closed-loop payments',
  b2bWhiteLabel: 'Loyalty white label',
};

const COST_LABEL = {
  cashbackPoints: 'Pulsar Points cashback',
  fxAbsorbed: 'FX absorbed (waivers)',
  aiCompute: 'AI inference',
  topupFunding: 'Card-funded top-up subsidy',
  wirexFloor: 'Wirex floor top-up',
  disputes: 'Disputes / chargebacks',
  walletOperations: 'Wallet operation subsidy',
  appAccountCac: 'App Account CAC',
  cardActivation: 'Card activation cost',
  fixedOpex: 'Fixed operating cost',
  cardProduction: 'Card production',
  referral: 'Referral program',
  moneyBack: 'Money Back program',
};

// Carbon → ice descending; emphasises top contributors.
const REVENUE_PALETTE = [
  '#1F1F1F',
  '#2D6694',
  '#437FAB',
  '#5C97C2',
  '#7AAED8',
  '#97C2EC',
  '#AECDED',
  '#BFDAF5',
  '#D8EAFA',
  '#EFF6FC',
];

// Carbon greys.
const COST_PALETTE = ['#1F1F1F', '#3D3D3D', '#525252', '#757575', '#ABABAB', '#D4D4D4'];

function fmtCompact(n) {
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(a)}`;
}

function fmtPct(n, d = 0) {
  return `${(n * 100).toFixed(d)}%`;
}

function buildSegments(lines, palette, labelMap) {
  const entries = Object.entries(lines || {})
    .filter(([, v]) => typeof v === 'number' && Math.abs(v) > 0.01)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  const total = entries.reduce((a, [, v]) => a + Math.abs(v), 0);
  return entries.map(([k, v], i) => ({
    key: k,
    label: labelMap[k] || k,
    value: v,
    color: palette[i % palette.length],
    pct: total > 0 ? Math.abs(v) / total : 0,
  }));
}

const VIEWS = [
  { k: 'both', label: 'Both' },
  { k: 'revenue', label: 'Revenue' },
  { k: 'cost', label: 'Cost' },
];

export default function RevenueCostStack({ rows }) {
  const [view, setView] = useState('both');
  const lines = useMemo(() => aggregateLines(rows || []), [rows]);

  const revSegs = useMemo(
    () => buildSegments(lines.revenue, REVENUE_PALETTE, REVENUE_LABEL),
    [lines.revenue],
  );
  const costSegs = useMemo(() => buildSegments(lines.cost, COST_PALETTE, COST_LABEL), [lines.cost]);

  const totalRev = revSegs.reduce((a, s) => a + Math.abs(s.value), 0);
  const totalCost = costSegs.reduce((a, s) => a + Math.abs(s.value), 0);
  const margin = totalRev - totalCost;

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <p className="caption text-carbon-400">Revenue / operating cost · 36-month total</p>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {(view === 'revenue' || view === 'both') && (
        <StackBlock title="Revenue" total={totalRev} segs={revSegs} />
      )}

      {(view === 'cost' || view === 'both') && (
        <StackBlock
          title="Cost"
          total={totalCost}
          segs={costSegs}
          className={view === 'both' ? 'mt-6 pt-6 border-t border-carbon-100' : ''}
        />
      )}

      {view === 'both' && (
        <div className="mt-6 pt-5 border-t border-carbon-100 flex items-baseline justify-between gap-3">
          <p className="caption text-carbon-500">3-year operating result</p>
          <div className="text-right">
            <p className={`h3 num-tabular ${margin >= 0 ? 'text-mint-800' : 'text-rose-600'}`}>
              {fmtCompact(margin)}
            </p>
            <p className="label text-carbon-500 num-tabular mt-1">
              {totalRev > 0 ? fmtPct(margin / totalRev, 1) : '—'} of revenue
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function StackBlock({ title, total, segs, className = '' }) {
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="label text-carbon-700">{title}</p>
        <p className="h3 num-tabular text-carbon-800">{fmtCompact(total)}</p>
      </div>

      {segs.length === 0 ? (
        <p className="body text-carbon-500">No values at the current scenario.</p>
      ) : (
        <>
          <div className="h-3 rounded-full overflow-hidden flex bg-carbon-50" aria-hidden="true">
            {segs.map((s) => (
              <div
                key={s.key}
                title={`${s.label}: ${fmtCompact(s.value)} (${fmtPct(s.pct, 0)})`}
                style={{ width: `${s.pct * 100}%`, background: s.color }}
              />
            ))}
          </div>
          <ul className="mt-4 space-y-2">
            {segs.map((s) => (
              <li key={s.key} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-sw shrink-0"
                    style={{ background: s.color }}
                    aria-hidden="true"
                  />
                  <span className="body text-carbon-800 truncate">{s.label}</span>
                </div>
                <div className="flex items-baseline gap-3 num-tabular shrink-0">
                  <span className="label text-carbon-400">{fmtPct(s.pct, 0)}</span>
                  <span className="label text-carbon-800">{fmtCompact(s.value)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  return (
    <div
      role="radiogroup"
      aria-label="Stack view"
      className="inline-flex p-1 rounded-full bg-carbon-50 border border-carbon-100"
    >
      {VIEWS.map((o) => {
        const active = view === o.k;
        return (
          <button
            key={o.k}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.k)}
            className={`label rounded-full transition-colors h-7 px-3 outline-none focus-visible:ring-2 focus-visible:ring-ice-400 ${
              active
                ? 'bg-white text-carbon-800 shadow-sm'
                : 'text-carbon-500 hover:text-carbon-800'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
